import React, { useEffect, useRef } from 'react';
import { theme } from '../../config/theme';
import { formatNumberForDisplay, formatNumberForInput, parseAmountForState } from '../../utils/formatters';
import { createTituloRow, MAX_TITULOS_POR_INSTRUCCION } from '../../utils/instruccionesTitulos';
import { TrashIcon } from '../Icons';

const ESPECIES_DATALIST_ID = 'especies-instr-list';

const styles = {
    container: { gridColumn: 'span 3', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    row: {
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 2.25rem',
        gap: '0 1.5rem', alignItems: 'center',
    },
    label: { display: 'block', fontSize: '0.875rem', fontWeight: '500', color: theme.colors.secondary },
    input: {
        width: '100%', padding: '0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: theme.typography.fontFamily,
    },
    removeButton: {
        background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
        color: theme.colors.errorText, justifySelf: 'center',
    },
    footer: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
    addButton: {
        fontFamily: theme.typography.fontFamily, backgroundColor: theme.colors.border, color: theme.colors.secondary,
        border: 'none', borderRadius: theme.borderRadius, padding: '0.45rem 0.85rem', fontSize: '0.8rem',
        fontWeight: '600', cursor: 'pointer',
    },
    hint: { margin: 0, fontSize: '0.75rem', color: theme.colors.textSecondary },
};

/*
 * Especies de una transferencia de títulos: una fila por especie, todas hacia la
 * misma contraparte. `onChange` recibe un updater (filas anteriores -> nuevas)
 * para que dos cambios seguidos no se pisen.
 */
const TitulosEditor = ({ rows = [], especies = [], onChange, isReadOnly = false }) => {
    const especieInputs = useRef(new Map());
    const pendingFocusRowId = useRef(null);
    const canRemove = !isReadOnly && rows.length > 1;
    const canAdd = !isReadOnly && rows.length < MAX_TITULOS_POR_INSTRUCCION;

    // Al agregar una fila el foco va a su especie, para seguir cargando con el teclado.
    useEffect(() => {
        const rowId = pendingFocusRowId.current;
        if (!rowId) return;
        pendingFocusRowId.current = null;
        especieInputs.current.get(rowId)?.focus();
    }, [rows]);

    const updateRow = (rowId, field, value) => {
        onChange(prev => prev.map(row => (row.rowId === rowId ? { ...row, [field]: value } : row)));
    };

    const addRow = () => {
        const nueva = createTituloRow();
        pendingFocusRowId.current = nueva.rowId;
        onChange(prev => [...prev, nueva]);
    };

    const removeRow = (rowId) => {
        onChange(prev => (prev.length > 1 ? prev.filter(row => row.rowId !== rowId) : prev));
    };

    return (
        <div style={styles.container}>
            <div style={styles.row}>
                <span style={styles.label}>Especie</span>
                <span style={styles.label}>Cantidad</span>
                <span />
            </div>

            {rows.map((row, index) => (
                <div key={row.rowId} style={styles.row}>
                    <input
                        ref={(el) => {
                            if (el) especieInputs.current.set(row.rowId, el);
                            else especieInputs.current.delete(row.rowId);
                        }}
                        list={ESPECIES_DATALIST_ID}
                        type="text"
                        aria-label={`Especie ${index + 1}`}
                        value={row.especie || ''}
                        onChange={(e) => updateRow(row.rowId, 'especie', e.target.value.toUpperCase())}
                        style={styles.input}
                        autoComplete="off"
                        disabled={isReadOnly}
                    />
                    <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Cantidad ${index + 1}`}
                        value={row.cantidadInputValue || ''}
                        onChange={(e) => updateRow(row.rowId, 'cantidadInputValue', e.target.value)}
                        onFocus={(e) => updateRow(row.rowId, 'cantidadInputValue', formatNumberForInput(e.target.value))}
                        onBlur={(e) => updateRow(
                            row.rowId, 'cantidadInputValue', formatNumberForDisplay(parseAmountForState(e.target.value)),
                        )}
                        style={styles.input}
                        disabled={isReadOnly}
                    />
                    {canRemove ? (
                        <button
                            type="button"
                            style={styles.removeButton}
                            onClick={() => removeRow(row.rowId)}
                            title="Quitar especie"
                            aria-label={`Quitar especie ${index + 1}`}
                        >
                            <TrashIcon />
                        </button>
                    ) : <span />}
                </div>
            ))}

            <datalist id={ESPECIES_DATALIST_ID}>
                {especies.map(esp => <option key={esp.id} value={esp.nombre} />)}
            </datalist>

            {!isReadOnly && (
                <div style={styles.footer}>
                    <button
                        type="button"
                        style={{ ...styles.addButton, ...(canAdd ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}
                        onClick={addRow}
                        disabled={!canAdd}
                        title={canAdd ? undefined : `Una instrucción admite hasta ${MAX_TITULOS_POR_INSTRUCCION} especies.`}
                    >
                        + Agregar especie
                    </button>
                    <p style={styles.hint}>
                        {rows.length > 1
                            ? `${rows.length} especies hacia la misma contraparte.`
                            : 'Si se transfieren varias especies a la misma contraparte, agregalas en esta instrucción.'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default TitulosEditor;
